import type {
    CommerceStore,
    Store,
    StoreSearchResult,
    StoreSearchResultUI,
    StoreDrawerSearchResult,
} from '@libs/common-node';
import { getLocale, logger, stringifyStoreId } from '@libs/core';
import { localCache } from './local-cache';

import { typedFetch } from './typedFetch';
import { getNextApiRoute } from './api';

export const CT_STORES_BASE_URL = getNextApiRoute('stores');

/**
 * We use a default TTL (Time To Live) of 1 hour for caching to ensure data remains relatively
 * fresh and to avoid unnecessary requests during this period.
 */
const DEFAULT_TTL = 60 * 60 * 1000; // TTL = 1hr

const locale = getLocale();

/**
 * Create URL friendly slug from city string
 * `City Name //=> city-name`
 */
const getCitySlug = (city = ''): string =>
    city.normalize().split(' ').join('-').toLowerCase();

export const getStoreDetailsPathSegment = ({
    City,
    StoreNumber,
}: Pick<Store, 'City' | 'StoreNumber'>): string => {
    const citySlug = getCitySlug(City);
    return `${citySlug}-store${stringifyStoreId(StoreNumber)}.html`;
};

/**
 * ## Constructs a URL pathname for store details using the provided store properties.
 *
 * This function takes a subset of the store properties: `CountryAbbreviation`, `StateProvinceAbbreviation`, `City`, and `StoreNumber`.
 * It performs the following steps:
 *
 * 1. Converts the `City` value into a URL-friendly slug using the `getCitySlug` function.
 * 2. Converts the `CountryAbbreviation` and `StateProvinceAbbreviation` to lowercase.
 * 3. Constructs and returns the pathname in the following format:
 *    `/stores/{country}/{state}/{citySlug}-store{StoreNumber}.html`
 */
export const getStoreDetailsPathname = (
    store: Pick<
        Store,
        | 'CountryAbbreviation'
        | 'StateProvinceAbbreviation'
        | 'City'
        | 'StoreNumber'
    >
): string => {
    const { CountryAbbreviation, StateProvinceAbbreviation } = store;
    return `/stores/${CountryAbbreviation?.toLocaleLowerCase()}/${StateProvinceAbbreviation?.toLowerCase()}/${getStoreDetailsPathSegment(
        store
    )}`;
};

/**
 * Extracts city slug and storeId from path segment
 */
export const parseStoreDetailsPathSegment = (
    pathSegment: string
): { storeId: string; citySlug: string } | null => {
    const pattern = /^([a-z]+(-[a-z]+)*)-store(\d+)\.html$/;
    const match = RegExp(pattern).exec(pathSegment);

    if (!match) {
        return null;
    }

    return {
        citySlug: match[1],
        storeId: match[3],
    };
};

/**
 * Validates that path segment matches store data
 */
export const validateStoreDetailsPathSegment = (
    pathSegment: string,
    store: Pick<Store, 'City' | 'StoreNumber'>
) => pathSegment === getStoreDetailsPathSegment(store);

/**
 * ## Retrieves details of a single store by its ID.
 *
 * This asynchronous function performs the following steps:
 *
 * 1. Constructs the request path using the base URL (`CT_STORES_BASE_URL`) and the provided store ID.
 * 2. Attempts to retrieve cached store data from localStorage using a caching mechanism with a default time-to-live (TTL).
 *    - If valid cached data is found, it returns the cached data immediately.
 * 3. If no valid cached data is available, it makes an API request using `typedFetch` to fetch the store details.
 *    - If the API response is successful, the data is cached using `setLocalCache` for future use.
 * 4. In case of an error or if the API response is null, the error is logged and the function returns null.
 */
export const getSingleStoreById = async (
    storeId: number
): Promise<Store | null> => {
    const path = `${CT_STORES_BASE_URL}/${storeId}`;

    // Try to get cached data first
    const { getLocalCache, setLocalCache } = localCache(DEFAULT_TTL);
    const cachedResult = await getLocalCache<Store>(path);

    // Guard
    if (cachedResult) {
        return cachedResult;
    }

    try {
        const response = await typedFetch<Store | null>(path);

        if (!response) {
            return null;
        }

        // Set localstorage response data
        await setLocalCache({ key: path, value: response });

        return response;
    } catch (error) {
        logger.error('Error getSingleStoreById. %o', error);
        return null;
    }
};

/**
 * ## Retrieves a single commerce store's details by its ID.
 *
 * This asynchronous function performs the following steps:
 *
 * 1. Constructs the request path using the base URL (`CT_STORES_BASE_URL`) and the provided store ID.
 * 2. Attempts to retrieve cached data from localStorage using a caching mechanism with a default time-to-live (TTL).
 *    - If valid cached data is found, it returns the cached result immediately.
 * 3. If no valid cached data is available, it makes an API request using `typedFetch` to fetch the commerce store data.
 *    - If the response is successful, the result is cached using `setLocalCache` for future use.
 * 4. In case of an error or if the API response is null, the error is logged and the function returns null.
 */
export const getSingleCommerceStoreById = async (
    storeId: number
): Promise<CommerceStore | null> => {
    const path = `${CT_STORES_BASE_URL}/commerce/${storeId}`;

    // Try to get cached data first
    const { getLocalCache, setLocalCache } = localCache(DEFAULT_TTL);
    const cachedResult = await getLocalCache<CommerceStore>(path);

    // Guard
    if (cachedResult) {
        return cachedResult;
    }

    try {
        const response = await typedFetch<CommerceStore | null>(path);

        if (!response) {
            return null;
        }

        // Set localstorage response data
        await setLocalCache({ key: path, value: response });

        return response;
    } catch (error) {
        logger.error('Error getSingleCommerceStoreById. %o', error);
        return null;
    }
};

/**
 * ## Fetches a list of store search results based on the client's IP address, using a caching mechanism.
 *
 * The function first attempts to retrieve cached data from localStorage using the key `closest?radius=30`
 * via the `localCache()` utility. If valid (non-expired) cached data is found, it is returned immediately.
 *
 * If no cached data exists, the function constructs the API URL with `getNextApiRoute()` and makes a network
 * request using `typedFetch()` to obtain the store search results. Upon a successful response, the data is cached
 * in localStorage (with a TTL of 24 hours) using `setLocalCache()` before being returned.
 *
 * In case of a fetch error or if the response is empty, the error is logged using `logger.error` and an empty array is returned.
 */
export const getStoresByIPAddress = async <
    T extends unknown[],
>(): Promise<T> => {
    const path = 'closest?radius=30';

    // Try to get cached data first
    const { getLocalCache, setLocalCache } = localCache(DEFAULT_TTL);
    const cachedResult = await getLocalCache<T>(path);

    // Guard
    if (cachedResult) {
        return cachedResult;
    }

    // Make request if there is no cached data
    try {
        const url = getNextApiRoute(path);
        const response = await typedFetch<T>(url);

        if (!response) {
            return [] as unknown as T;
        }

        // Set localstorage response data
        await setLocalCache({ key: path, value: response });

        return response;
    } catch (error) {
        logger.error('Error getStoresByIPAddress. %o', error);
        return [] as unknown as T;
    }
};

/**
 * ## Retrieves the closest stores based on the provided latitude and longitude coordinates.
 *
 * This asynchronous function follows these steps:
 *
 * 1. Constructs an API path using the given `lat` and `long` values.
 * 2. Attempts to fetch cached data from localStorage using a caching mechanism with a default time-to-live (TTL).
 *    - If valid cached data is found, it returns the cached result immediately.
 *
 * 3. If no valid cached data is found, the function:
 *    - Constructs the complete API URL using `getNextApiRoute`.
 *    - Fetches the data using `typedFetch` which returns data of type `T`.
 *    - If the response is valid, caches the response in localStorage for future use.
 *
 * 4. In case of an error or if no response is received, it logs the error and returns an empty array cast as type `T`.
 */
export const getStoresByCoordinates = async <T extends unknown[]>(
    lat: number,
    lng: number,
    sku?: string,
    radius?: number
): Promise<T> => {
    const skuParam = sku ? `&sku=${sku}` : '';
    const radiusParam = radius ? `&radius=${radius}` : '';
    const path = `${CT_STORES_BASE_URL}?lat=${lat}&lng=${lng}&locale=${locale}${skuParam}${radiusParam}`;

    // Try to get cached data first
    const { getLocalCache, setLocalCache } = localCache(DEFAULT_TTL);
    const cachedResult = await getLocalCache<T>(path);

    // Guard
    if (cachedResult) {
        return cachedResult;
    }

    // Make request if there is no cached data
    try {
        const response = await typedFetch<T>(path);

        if (!response) {
            return [] as unknown as T;
        }

        // Set localstorage response data
        await setLocalCache({ key: path, value: response });

        return response;
    } catch (error) {
        logger.error('Error getStoresByCoordinates. %o', error);
        return [] as unknown as T;
    }
};

export const getClosesStoresByCoordinates = async <T extends unknown[]>(
    lat: number,
    long: number
): Promise<T> => {
    const path = `closest?lat=${lat}&lng=${long}`;

    // Try to get cached data first
    const { getLocalCache, setLocalCache } = localCache(DEFAULT_TTL);
    const cachedResult = await getLocalCache<T>(path);

    // Guard
    if (cachedResult) {
        return cachedResult;
    }

    // Make request if there is no cached data
    try {
        const url = getNextApiRoute(path);
        const response = await typedFetch<T>(url);

        if (!response) {
            return [] as unknown as T;
        }

        // Set localstorage response data
        await setLocalCache({ key: url, value: response });

        return response;
    } catch (error) {
        logger.error('Error getClosesStoresByCoordinates. %o', error);
        return [] as unknown as T;
    }
};

/**
 * ## Retrieves a list of stores based on a search query.
 *
 * This asynchronous function performs the following steps:
 *
 * 1. Constructs the request URL using the provided `searchQuery`, the constant `CT_STORES_BASE_URL`, the current `locale`,
 *    and an optional `radius` parameter (defaulting to 30 if not provided).
 *
 * 2. Attempts to retrieve cached data from localStorage using a caching mechanism with a specified time-to-live (TTL).
 *    - If valid cached data is found, it is returned immediately.
 *
 * 3. If no valid cached data is available, the function makes an API request using `typedFetch`.
 *    - If the response is successful, the result is cached using `setLocalCache` for future requests.
 *
 * 4. In case of an error during the API call, the error is logged and an empty array is returned.
 */
export const getStoresByQuery = async (
    searchQuery: string,
    radius?: number
): Promise<StoreSearchResultUI[]> => {
    const radiusParam = radius ?? 30;
    const path = `${CT_STORES_BASE_URL}/search/${searchQuery}?locale=${locale}&radius=${radiusParam}`;

    // Try to get cached data first
    const { getLocalCache, setLocalCache } = localCache(DEFAULT_TTL);
    const cachedResult = await getLocalCache<StoreSearchResultUI[]>(path);

    // Guard
    if (cachedResult) {
        return cachedResult;
    }

    // Make request if there is no cached data
    try {
        const response = await typedFetch<StoreSearchResultUI[]>(path);

        if (!response) {
            return [];
        }

        // Set localstorage response data
        await setLocalCache({ key: path, value: response });

        return response;
    } catch (error) {
        logger.error('Error getStoresByQuery. %o', error);
        return [];
    }
};

/**
 * ## Retrieves a list of stores based on a geolocation query.
 *
 * This asynchronous function performs the following steps:
 *
 * 1. It constructs an API path by combining the provided `geolocationQuery` with an optional `radius` parameter.
 * 2. It tries to retrieve cached store search results from localStorage using a caching mechanism with a TTL.
 *    - The caching mechanism is provided by the `localCache` function and uses a default TTL (24 hours) unless another is specified.
 * 3. If valid cached data is found (i.e. not expired), the function immediately returns the cached results.
 * 4. If no valid cached data is available, it:
 *    - Constructs the complete API URL using `getNextApiRoute`.
 *    - Fetches the store data using the `typedFetch` function.
 *    - If the fetch is successful and returns data, it caches the response using `setLocalCache` for future use.
 * 5. In case of any error during the API call, the error is logged and the function returns an empty array.
 */
export const getStoresByGeolocation = async (
    geolocationQuery: string,
    radius?: number
): Promise<StoreSearchResult[]> => {
    const radiusParam = radius ? `?radius=${radius}` : '';
    const path = `stores/${geolocationQuery}${radiusParam}`;

    // Try to get cached data first
    const { getLocalCache, setLocalCache } = localCache(DEFAULT_TTL);
    const cachedResult = await getLocalCache<StoreSearchResult[]>(path);

    // Guard: if cache exists, return it
    if (cachedResult) {
        return cachedResult;
    }

    // Make request if there is no cached data
    try {
        const url = getNextApiRoute(path);
        const response = await typedFetch<StoreDrawerSearchResult>(url);

        if (!response) {
            return [];
        }

        // Set localstorage response data
        await setLocalCache({ key: url, value: response });

        return response.searchResult;
    } catch (error) {
        logger.error('Error getStoresByGeolocation. %o', error);
        return [];
    }
};
